import { useId } from 'react';
import { useRecoilState, useRecoilValue } from 'recoil';
import {
  StepValidationContext,
  useStepValidation,
} from '@zurich/common/contexts';
import { Stack } from '@zurich/ui/atoms';
import { styled } from '@zurich/ui/legacy/utils';
import { isAutoArea } from '~/lib/types';
import { damageFieldsValidationSelectorFamily, editDamageId } from '~/state';
import { DocumentDamageDataEntryHeader } from './DocumentDamageDataEntryHeader';
import { DocumentDamageOverview } from './DocumentDamageOverview';

type DocumentDamageDataEntryProps = {
  resetImageBlob: (id: string | undefined) => void;
  handleShowDamageContent: () => void;
  stepName: string;
};

export function DocumentDamageDataEntry({
  resetImageBlob,
  handleShowDamageContent,
  stepName,
}: DocumentDamageDataEntryProps) {
  const uniqueId = useId();
  const { stepValidation, triggerStepValidation } = useStepValidation();
  const [editDamageIdValue, setEditDamageIdValue] =
    useRecoilState(editDamageId);

  const damageId = editDamageIdValue
    ? `${editDamageIdValue}`
    : `${stepName}${uniqueId}`;

  const activeStepValidation = useRecoilValue(
    damageFieldsValidationSelectorFamily(damageId)
  );

  function handleConfirmation() {
    if (activeStepValidation.valid) {
      handleShowDamageContent();
      setEditDamageIdValue(undefined);
      return;
    }
    triggerStepValidation();
  }

  return (
    <StepValidationContext.Provider value={stepValidation}>
      <ContainerStyled>
        <Stack rowGap={0} alignItems="center">
          <Stack
            flexDirection="column"
            rowGap={1}
            alignItems="flex-start"
            justifyContent="flex-start"
            width="100%"
          >
            <DocumentDamageDataEntryHeader
              resetImageBlob={resetImageBlob}
              handleShowDamageContent={handleShowDamageContent}
              damageId={damageId}
            />
          </Stack>
        </Stack>
        {isAutoArea(stepName) && (
          <DocumentDamageOverview
            handleConfirmation={handleConfirmation}
            id={damageId}
            step={stepName}
          />
        )}
      </ContainerStyled>
    </StepValidationContext.Provider>
  );
}

const ContainerStyled = styled(Stack)(({ theme }) => ({
  rowGap: theme.spacing(1),
  height: '100%',
  paddingBottom: '24px',
}));
